/**
 * The severity filter — the plan's *"severity filter — icon+text không chỉ màu"*.
 *
 * ── Three channels, and the third one is optional ───────────────────────────
 *
 * `DESIGN.md` §4 and spec 01 §19: severity is carried by **icon silhouette**, **text
 * label** and colour, in that order of priority, and colour is never alone. Every
 * toggle here renders `Icon` beside the word, and the icon is `SEVERITY_ICON`'s — the
 * same octagon / triangle / circle / tick / info the `Badge` on the row uses, so the
 * filter and the thing it selects are legible as the same vocabulary in greyscale.
 *
 * **Selection is not carried by colour either.** `aria-pressed` announces it, the
 * word *(đang chọn)* is in the accessibility tree, and the class only paints. A
 * filter whose only "on" signal was a background would be a control a colour-blind
 * operator cannot read the state of — which is the same defect one level up from the
 * one this component exists to prevent.
 *
 * ── Single-select, because the server is ────────────────────────────────────
 *
 * `GET /alerts` takes one `severity` and one `status`; the router refuses a repeated
 * parameter with `must be a single value`. So these are radio-shaped, not checkbox-
 * shaped. Offering multi-select would mean OR-ing pages in the browser over a
 * **cursor**-paginated endpoint that publishes no total — a filter whose answer is
 * wrong and has nothing to be checked against.
 *
 * The "all" option sends **no parameter at all** rather than a wildcard value, which
 * is what `qs` does with `undefined`.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import type { AlertSeverity, AlertStatus } from '../../api/types';
import { ALERT_SEVERITIES, BADGE_SEVERITY } from '../../alerts/inbox';
import { Icon, SEVERITY_ICON } from '../../states/icons';

const SEVERITY_LABEL: Readonly<Record<AlertSeverity, MessageKey>> = {
  informational: 'severity.info',
  low: 'severity.low',
  medium: 'severity.medium',
  high: 'severity.high',
  critical: 'severity.critical',
};

/**
 * The three statuses this filter offers, and `resolved` is deliberately not one.
 *
 * `openapi.yaml` lists it in the enum and says in as many words that **this version
 * produces none**: nothing resolves an alert, there is no `resolved_at`, and an
 * acknowledged row cannot be updated again. `?status=resolved` is accepted and
 * matches nothing. A filter option that can only ever return an empty page is a
 * screen telling an operator there are no resolved alerts when the truth is that the
 * concept does not exist yet — which is exactly the "empty is not the same as
 * unavailable" rule of `PRODUCT.md` §4, applied to a control instead of a state.
 */
const OFFERED_STATUSES = ['open', 'acknowledged', 'suppressed'] as const;

type OfferedStatus = (typeof OFFERED_STATUSES)[number];

const STATUS_LABEL: Readonly<Record<OfferedStatus, MessageKey>> = {
  open: 'alerts.status.open',
  acknowledged: 'alerts.status.acknowledged',
  suppressed: 'alerts.status.suppressed',
};

export interface SeverityFilterProps {
  readonly severity: AlertSeverity | null;
  readonly status: AlertStatus | null;
  readonly onSeverity: (value: AlertSeverity | null) => void;
  readonly onStatus: (value: AlertStatus | null) => void;
}

function Toggle({
  pressed,
  label,
  onPress,
  testId,
  icon,
}: {
  readonly pressed: boolean;
  readonly label: string;
  readonly onPress: () => void;
  readonly testId: string;
  readonly icon?: ReactNode;
}): ReactNode {
  const { t } = useI18n();
  return (
    <button
      className="wp-toggle"
      type="button"
      aria-pressed={pressed}
      data-testid={testId}
      onClick={onPress}
    >
      {icon}
      <span>{label}</span>
      {pressed ? <span className="wp-visually-hidden"> {t('filters.selected')}</span> : null}
    </button>
  );
}

export function SeverityFilter({
  severity,
  status,
  onSeverity,
  onStatus,
}: SeverityFilterProps): ReactNode {
  const { t } = useI18n();
  return (
    <div className="wp-c-bar" data-testid="l5-alert-filters">
      <fieldset className="wp-c-bar__group">
        <legend className="wp-c-bar__legend">{t('alerts.filter.severity')}</legend>
        <div className="wp-toggle-group">
          <Toggle
            pressed={severity === null}
            label={t('filters.all')}
            onPress={() => {
              onSeverity(null);
            }}
            testId="l5-severity-all"
          />
          {ALERT_SEVERITIES.map((value) => (
            <Toggle
              key={value}
              pressed={severity === value}
              label={t(SEVERITY_LABEL[value])}
              onPress={() => {
                onSeverity(value);
              }}
              testId={`l5-severity-${value}`}
              icon={<Icon name={SEVERITY_ICON[BADGE_SEVERITY[value]]} className="wp-badge__icon" />}
            />
          ))}
        </div>
      </fieldset>

      <fieldset className="wp-c-bar__group">
        <legend className="wp-c-bar__legend">{t('alerts.filter.status')}</legend>
        <div className="wp-toggle-group">
          <Toggle
            pressed={status === null}
            label={t('filters.all')}
            onPress={() => {
              onStatus(null);
            }}
            testId="l5-status-all"
          />
          {OFFERED_STATUSES.map((value) => (
            <Toggle
              key={value}
              pressed={status === value}
              label={t(STATUS_LABEL[value])}
              onPress={() => {
                onStatus(value);
              }}
              testId={`l5-status-${value}`}
            />
          ))}
        </div>
      </fieldset>
      <p className="wp-small wp-muted wp-c-bar__note" data-testid="l5-filter-note">
        {t('alerts.filter.serverSide')}
      </p>
    </div>
  );
}
